import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface TemperatureReading {
  timestamp: string;
  temperature: number;
}

interface BlockData {
  block_id: string;
  data: TemperatureReading[];
}

interface ModuleTempGraphProps {
  plantId: string;
  date: string;
}

const BLOCK_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
  '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2',
  '#A3E4D7', '#F9E79F', '#D5A6BD', '#AED6F1', '#A9DFBF'
];

export const ModuleTempGraph: React.FC<ModuleTempGraphProps> = ({ plantId, date }) => {
  const [data, setData] = useState<BlockData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleBlocks, setVisibleBlocks] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchTemperatureData();
  }, [plantId, date]);

  const fetchTemperatureData = async () => {
    try {
      setLoading(true);
      
      // Mock data for demonstration
      const mockData = {
        blocks: Array.from({length: 20}, (_, i) => ({
          block_id: `BLOCK-${(i + 1).toString().padStart(2, '0')}`,
          data: Array.from({length: 12}, (_, h) => ({
            timestamp: `2025-10-17T${(h + 6).toString().padStart(2, '0')}:00:00Z`,
            temperature: 20 + Math.random() * 25 + Math.sin(h * 0.5) * 10
          }))
        }))
      };
      
      setData(mockData.blocks);
      
      // Initially show first 10 blocks
      const initialVisible = new Set(mockData.blocks.slice(0, 10).map((b: BlockData) => b.block_id));
      setVisibleBlocks(initialVisible);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const toggleBlockVisibility = (blockId: string) => {
    const newVisible = new Set(visibleBlocks);
    if (newVisible.has(blockId)) {
      newVisible.delete(blockId);
    } else {
      newVisible.add(blockId);
    }
    setVisibleBlocks(newVisible);
  };

  const chartData = {
    labels: data.length > 0 ? data[0].data.map(reading => 
      new Date(reading.timestamp).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    ) : [],
    datasets: data
      .filter(block => visibleBlocks.has(block.block_id))
      .map((block, index) => ({
        label: block.block_id,
        data: block.data.map(reading => reading.temperature),
        borderColor: BLOCK_COLORS[index % BLOCK_COLORS.length],
        backgroundColor: BLOCK_COLORS[index % BLOCK_COLORS.length] + '20',
        borderWidth: 2,
        pointRadius: 1,
        pointHoverRadius: 4,
        tension: 0.1
      }))
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // We'll use custom legend
      },
      title: {
        display: true,
        text: 'Module Temperature Trends by Block'
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}°C`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Temperature (°C)'
        },
        min: 0,
        max: 60
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Module Temperature Analysis
        </h3>
        <p className="text-sm text-gray-600">
          Showing temperature trends for {visibleBlocks.size} of {data.length} blocks
        </p>
      </div>

      {/* Block Toggle Controls */}
      <div className="mb-4 max-h-32 overflow-y-auto">
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
          {data.map((block, index) => (
            <button
              key={block.block_id}
              onClick={() => toggleBlockVisibility(block.block_id)}
              className={`px-2 py-1 text-xs rounded border transition-colors ${
                visibleBlocks.has(block.block_id)
                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                  : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
              }`}
              style={{
                borderColor: visibleBlocks.has(block.block_id) 
                  ? BLOCK_COLORS[index % BLOCK_COLORS.length] 
                  : undefined
              }}
            >
              {block.block_id}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="h-96">
        <Line data={chartData} options={options} />
      </div>

      {/* Export Options */}
      <div className="mt-4 flex justify-end space-x-2">
        <button className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded border">
          Export CSV
        </button>
        <button className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded border">
          Export PNG
        </button>
      </div>
    </div>
  );
};